import type { BdDateInterval } from '@bindoc-projects/time-domain-lib';
import type {
  FilterConditionDto,
} from './condition';
import {
  BdCondition,
  BdConditionComparison
} from './condition';
import { BdDismissalDateTimeCondition } from './time-condition';
import { endOfDay } from 'date-fns/endOfDay';
import { formatISO } from 'date-fns/formatISO';
import { startOfDay } from 'date-fns/startOfDay';
import { TZDate } from '@date-fns/tz';
import { BdRangeCondition } from './range.condition';

export const BD_DATE_INTERVAL_TIME_CONDITION_ID = 'dateInterval';

export class DateRangeAsCombinationCondition extends BdCondition {
  constructor(
    id: string,
    public interval: BdDateInterval,
  ) {
    super(id);
  }

  toJSON(): FilterConditionDto {
    return {
      and: {
        conditions: [
          new BdDismissalDateTimeCondition(
            this.interval.start,
            BdConditionComparison.GREATER_OR_EQUAL,
            (startDate) => DateRangeCondition.formatAsUTC(startDate, date => startOfDay(date)),
          ).toJSON(),
          new BdDismissalDateTimeCondition(
            this.interval.end,
            BdConditionComparison.LESS_OR_EQUAL,
            (endDate) => DateRangeCondition.formatAsUTC(endDate, date => endOfDay(date)),
          ).toJSON(),
        ],
      }
    };
  }
}

export abstract class DateRangeCondition extends BdRangeCondition<string> {
  /**
   * Transform date with time zone info to UTC.
   *
   * Time is stored in dashboard database in UTC, but start and end of day needs
   * to be calculated in original time zone.
   * So this method transforms the date range in a first step from UTC to
   * original time zone.
   * In a next step the range is transformed to the start of the day of the range
   * start and to the end of the day of the range end.
   *
   * @see https://bindoc.atlassian.net/browse/BDA-4079
   */
  public static formatAsUTC(date: Date, dateManipulationFn: (date: TZDate) => TZDate): string {
    return formatISO(
      dateManipulationFn(
        new TZDate(date.toISOString(), 'Etc/UTC').withTimeZone('Europe/Berlin'),
      ).withTimeZone('Etc/UTC'),
    );
  }

  constructor(id: string, interval: BdDateInterval) {
    super(
      id,
      DateRangeCondition.formatAsUTC(interval.start, date => startOfDay(date)),
      DateRangeCondition.formatAsUTC(interval.end, date => endOfDay(date)),
    );
  }
}
